import {Component, inject, OnInit} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {SelectComponent} from '../../_ui/components/controls/select/select.component';
import {AuditHttpService} from './data-access/audit-http.service';
import {
  AuditEntry, AuditOutcome, auditOutcomeClass, auditOutcomeLabel, AuditQuery, ResolvedNames
} from './shared/models/audit.model';
import {CompanyTimeZoneService} from '../../_shared/time/company-time-zone.service';
import {addCalendarDays, companyDayStartInstant, DayKey} from '../../_shared/time/company-day';

/**
 * Audit-log viewer for the {@link Roles.Auditor} role (also reachable by PlatformOwner/CompanyOwner who hold it).
 * Lists the append-only audit trail newest-first with server-side filters (date range, category) and a client-side
 * outcome filter, and **resolves the GUID fields to human names** — actor display names are stamped at write time,
 * while company/shard/impersonator ids are resolved via the monolith ({@link AuditHttpService.resolveNames}). The
 * read API is company-scoped server-side, so a non-platform auditor only ever sees their own company's entries.
 *
 * Date-range filter (#653): "Von"/"Bis" are COMPANY calendar days (`CompanyTimeZoneService`), never the browser's
 * — `AuditController.List` (`CheqMe.Server.Services.Auditing`) filters `OccurredAtUtc >= from && OccurredAtUtc <=
 * to`, both bounds INCLUSIVE, so "Bis" must reach one second before the next company day starts, not local
 * midnight of the typed string (which used to drop the last second of "Bis" and shift both bounds by the
 * browser's own offset from the company's).
 *
 * Two sessions cannot read the company zone, and both fall back to `Europe/Berlin`:
 * - a PlatformOwner with no company in context gets a 400 from `GET /Company/CompanyTimeZone/Get` (that endpoint
 *   requires a company session);
 * - a session holding only an ADDITIVE role such as {@link Roles.Auditor} gets a 403, because the endpoint admits
 *   only ShardAdmin, Employee, Payroll, CompanyOwner and PlatformOwner
 *   (`API/Controller/Company/CompanyTimeZoneController.cs`).
 *
 * `CompanyTimeZoneService` treats every load failure the same way, falling back to the server's own default
 * `Europe/Berlin` rather than the browser's zone. That is accepted here: a platform-wide
 * audit view has no single "company day" to anchor on, and Berlin (the server's own default elsewhere) is a more
 * predictable fallback than whatever zone the viewer's laptop happens to be in.
 */
@Component({
  selector: 'app-audit-log',
  standalone: true,
  imports: [DatePipe, NgClass, FormsModule, SelectComponent],
  template: `
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-6xl flex flex-col gap-4">

        <div class="flex items-center gap-3">
          <svg class="h-6 w-6 text-theme-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
          <h1 class="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Audit-Protokoll</h1>
        </div>

        <!-- Filters -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Von</span>
            <input type="date" [(ngModel)]="fromDate" class="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500"/>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Bis</span>
            <input type="date" [(ngModel)]="toDate" class="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500"/>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Kategorie</span>
            <input type="text" [(ngModel)]="category" placeholder="z. B. RoleGrant" class="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 placeholder-gray-300"/>
          </label>
          <!-- The read API has always supported these two; the screen simply never offered them, so the one
               question this trail is most often asked — "who did something to THIS company?" — could only be
               answered by fetching a category and reading 200 rows. Free text rather than a picker because the
               vocabulary is open: every feature owns its own TargetType strings (#279). -->
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Zielart</span>
            <input type="text" [(ngModel)]="targetType" placeholder="z. B. Company" class="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 placeholder-gray-300"/>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Ziel-ID</span>
            <input type="text" [(ngModel)]="targetId" placeholder="ID des Ziels" class="rounded-xl border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 placeholder-gray-300"/>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-xs font-bold uppercase tracking-wider text-gray-500">Ergebnis</span>
            <!-- cqme-select, not a native <select>: the design-system control is the house rule, and a native
                 one additionally exposes its options to type-ahead. The option's "value" carries the
                 AuditOutcome ordinal, which is what the wire and the client-side filter both compare on.
                 (No backticks anywhere in this template: it is a template literal.) -->
            <cqme-select size="md" [options]="outcomeOptions" [labelKey]="'label'" [valueKey]="'value'"
                         ariaLabel="Ergebnis" [current]="outcomeFilter ?? -1"
                         (currentChange)="setOutcome($event)"/>
          </label>
          <div class="flex items-end">
            <button type="button" (click)="load()" [disabled]="loading"
                    class="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-theme-primary-700 active:scale-95 disabled:opacity-40">
              Aktualisieren
            </button>
          </div>
        </div>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error) {
          <div class="rounded-2xl border border-red-100 bg-red-50 p-4 shadow-sm">
            <p class="text-sm font-semibold text-red-600">Das Audit-Protokoll konnte nicht geladen werden.</p>
            <button (click)="load()" class="mt-2 rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-600 transition-all hover:bg-red-200">Erneut versuchen</button>
          </div>
        } @else if (visible.length === 0) {
          <div class="rounded-2xl border border-gray-100 bg-white p-10 text-center shadow-sm">
            <p class="text-sm font-medium text-gray-500">Keine Einträge für die gewählten Filter.</p>
          </div>
        } @else {
          <p class="text-xs font-medium text-gray-500">{{ visible.length }} Einträge (neueste zuerst)</p>
          <ul class="flex flex-col gap-2">
            @for (e of visible; track e.id) {
              <li class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="flex items-center gap-2">
                    <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold" [ngClass]="outcomeClass(e.outcome)">{{ outcomeLabel(e.outcome) }}</span>
                    <span class="text-sm font-bold tracking-tight text-gray-800">{{ e.category }} · {{ e.action }}</span>
                  </div>
                  <span class="text-xs font-medium text-gray-500">{{ e.occurredAtUtc | date: 'dd.MM.yyyy HH:mm:ss' }}</span>
                </div>

                <!-- Descriptor / value pairs — one uniform style for every label, one for every value. -->
                <div class="mt-2 flex flex-col gap-1">
                  <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span [class]="desc">von</span>
                    <span [class]="val">{{ actorName(e) }}</span>
                    @if (e.actorRole) {
                      <span class="rounded-full bg-theme-primary-50 px-2 py-0.5 text-xs font-semibold text-theme-primary-600">{{ e.actorRole }}</span>
                    }
                    @if (impersonatorName(e); as imp) {
                      <span [class]="desc">· im Auftrag von</span>
                      <span [class]="val">{{ imp }}</span>
                    }
                  </div>

                  @if (e.targetType || e.targetId) {
                    <div class="flex flex-wrap items-center gap-x-1.5">
                      <span [class]="desc">Ziel</span>
                      <span [class]="val + ' break-all'">{{ e.targetType }}{{ e.targetId ? ' · ' + e.targetId : '' }}</span>
                    </div>
                  }

                  <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                    @if (companyName(e); as c) {
                      <span class="flex items-center gap-x-1.5"><span [class]="desc">Firma</span><span [class]="val">{{ c }}</span></span>
                    }
                    @if (shardName(e); as s) {
                      <span class="flex items-center gap-x-1.5"><span [class]="desc">Standort</span><span [class]="val">{{ s }}</span></span>
                    }
                    @if (e.authSource) {
                      <span class="flex items-center gap-x-1.5"><span [class]="desc">Anmeldung</span><span [class]="val">{{ e.authSource }}</span></span>
                    }
                  </div>
                </div>

                @if (e.detail) {
                  <p class="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600 break-words whitespace-pre-wrap">{{ e.detail }}</p>
                }
              </li>
            }
          </ul>
        }
      </div>
    </div>
  `
})
export class AuditLogComponent implements OnInit {
  protected loading = true;
  protected error = false;
  protected entries: AuditEntry[] = [];
  protected names: ResolvedNames = {users: {}, companies: {}, shards: {}};

  // Filters. `<input type="date">`'s own value IS already a "yyyy-MM-dd" company day key — no `Date` involved.
  protected fromDate: DayKey | '' = '';
  protected toDate: DayKey | '' = '';
  protected category = '';
  protected targetType = '';
  protected targetId = '';
  protected outcomeFilter: AuditOutcome | null = null;

  /**
   * The entries of the outcome picker.
   *
   * `-1` stands for "Alle" because `cqme-select` binds a value key on an object list and cannot express "no
   * value chosen" as a selectable option — `undefined` is how the control says *nothing is chosen*, which
   * renders as a blank box rather than as the default. The sentinel is mapped back to `null` in
   * {@link setOutcome}, so nothing outside this pair ever sees it.
   */
  protected readonly outcomeOptions = [
    {value: -1, label: 'Alle'},
    {value: AuditOutcome.Success, label: auditOutcomeLabel(AuditOutcome.Success)},
    {value: AuditOutcome.Denied, label: auditOutcomeLabel(AuditOutcome.Denied)},
    {value: AuditOutcome.Failure, label: auditOutcomeLabel(AuditOutcome.Failure)},
    {value: AuditOutcome.Warning, label: auditOutcomeLabel(AuditOutcome.Warning)}
  ];

  protected readonly outcomeLabel = auditOutcomeLabel;
  protected readonly outcomeClass = auditOutcomeClass;

  /**
   * One shared style for every field label (descriptor) and one for every field value, so they never drift.
   *
   * `gray-500`, not `gray-400`: at this size the lighter shade does not reach WCAG AA on the white card these
   * labels sit on, and every one of them names the field beside it — a reader who cannot make out "Ziel" cannot
   * tell which id they are looking at.
   */
  protected readonly desc = 'text-sm font-medium text-gray-500';
  protected readonly val = 'text-sm font-semibold text-gray-700';

  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  constructor(private http: AuditHttpService) {
  }

  ngOnInit(): void {
    // Fire the zone lookup alongside the first (unfiltered) load, so it is already resolved by the time the
    // viewer picks a date range and clicks "Aktualisieren".
    this.companyTimeZone.load().subscribe();
    this.load();
  }

  /** Maps the picker's "Alle" sentinel back to the null the filter itself is written in terms of. */
  protected setOutcome(value: number | number[] | undefined): void {
    const chosen = Array.isArray(value) ? value[0] : value;
    this.outcomeFilter = chosen === undefined || chosen === -1 ? null : chosen as AuditOutcome;
  }

  /** Entries after applying the client-side outcome filter (date/category/target are server-side). */
  protected get visible(): AuditEntry[] {
    return this.outcomeFilter == null ? this.entries : this.entries.filter(e => e.outcome === this.outcomeFilter);
  }

  load(): void {
    this.loading = true;
    this.error = false;

    const timeZone = this.companyTimeZone.timeZone();
    const query: AuditQuery = {take: 200};
    if (this.fromDate) query.from = companyDayStartInstant(this.fromDate, timeZone).toISOString();
    if (this.toDate) {
      // Inclusive "Bis": one millisecond before the next company day starts, so the whole "Bis" day — including
      // its last second — is covered.
      const exclusiveEnd = companyDayStartInstant(addCalendarDays(this.toDate, 1), timeZone);
      query.to = new Date(exclusiveEnd.getTime() - 1).toISOString();
    }
    if (this.category.trim()) query.category = this.category.trim();
    if (this.targetType.trim()) query.targetType = this.targetType.trim();
    if (this.targetId.trim()) query.targetId = this.targetId.trim();

    this.http.list(query).subscribe({
      next: entries => {
        this.entries = entries;
        this.resolve(entries);
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  /** Resolve the GUID fields shown in the list (actor without a stamped name, impersonator, company, shard). */
  private resolve(entries: AuditEntry[]): void {
    const userIds = new Set<string>();
    const companyIds = new Set<string>();
    const shardIds = new Set<string>();
    for (const e of entries) {
      if (e.actorUserId && !e.actorDisplayName) userIds.add(e.actorUserId);
      if (e.impersonatedBy) userIds.add(e.impersonatedBy);
      if (e.companyId) companyIds.add(e.companyId);
      if (e.shardId) shardIds.add(e.shardId);
    }
    if (userIds.size + companyIds.size + shardIds.size === 0) return;

    this.http.resolveNames({
      userIds: [...userIds], companyIds: [...companyIds], shardIds: [...shardIds]
    }).subscribe({
      next: names => this.names = names,
      error: () => { /* names stay as ids — best-effort */ }
    });
  }

  protected actorName(e: AuditEntry): string {
    if (e.actorDisplayName) return e.actorDisplayName;
    if (e.actorUserId) return this.names.users[e.actorUserId] ?? this.shortId(e.actorUserId);
    return 'System';
  }

  protected impersonatorName(e: AuditEntry): string | null {
    return e.impersonatedBy ? (this.names.users[e.impersonatedBy] ?? this.shortId(e.impersonatedBy)) : null;
  }

  protected companyName(e: AuditEntry): string | null {
    return e.companyId ? (this.names.companies[e.companyId] ?? this.shortId(e.companyId)) : null;
  }

  protected shardName(e: AuditEntry): string | null {
    return e.shardId ? (this.names.shards[e.shardId] ?? this.shortId(e.shardId)) : null;
  }

  /** A short, recognisable stand-in when a name can't be resolved (e.g. a deleted/foreign entity). */
  private shortId(id: string): string {
    return id.length > 8 ? id.slice(0, 8) + '…' : id;
  }
}
